import { api, type FetchOptions, type MutOpts } from "@/api"
import { type UseMutationOptions, queryOptions, useMutation, useQuery } from "@tanstack/react-query"

import { queryClient } from "@/lib/query-client"

const facilityKeys = {
  all: ["facilities"] as const,
  list: () => [...facilityKeys.all, "list"] as const,
  lists: <TQuery>(query?: TQuery) =>
    [...facilityKeys.list(), { query }] as const,
  faciliittyTypeList: <TQuery>(query?: TQuery) =>
    [...facilityKeys.list(), "type", { query }] as const,
  create: () => [...facilityKeys.list(), "create"] as const,
  update: () => [...facilityKeys.list(), "update"] as const,
  timezones: () => [...facilityKeys.all, "timezones"] as const,
  geofence: (facilityId: number) => [...facilityKeys.all, "geofence", facilityId] as const,
}

export const facilitiesQueryOptions = (
  fetchOpts?: FetchOptions<"get", "/facilities/">
) => {
  const query = fetchOpts?.params?.query

  return queryOptions({
    queryKey: facilityKeys.lists({ query }),
    queryFn: () =>
      api.GET("/facilities/", { params: { query } }).then(({ data }) => data),
    staleTime: 30_000,
  })
}

export const useFacilitiesQuery = (
  fetchOpts?: FetchOptions<"get", "/facilities/">
) => {
  return useQuery(facilitiesQueryOptions(fetchOpts))
}

export const useFacilityTypesQuery = (
  fetchOpts?: FetchOptions<"get", "/facility-types/">
) => {
  const query = fetchOpts?.params?.query

  return useQuery({
    queryKey: facilityKeys.faciliittyTypeList(query),
    queryFn: () => {
      return api
        .GET("/facility-types/", { params: { query } })
        .then(({ data }) => data)
    },
    staleTime: 10 * 60 * 1000, // 10 min — facility types rarely change
    gcTime: 30 * 60 * 1000,   // 30 min — must exceed staleTime so the entry isn't evicted while still fresh
  })
}

export const useTimezonesQuery = () => {
  return useQuery({
    queryKey: facilityKeys.timezones(),
    queryFn: () => {
      return api.GET("/timezones/").then(({ data }) => data)
    },
    staleTime: 10 * 60 * 60 * 1000, // 10 hours in milliseconds
    gcTime: 10 * 60 * 60 * 1000, // 10 hours in milliseconds
  })
}

export const useCreateFacilityMutation = (
  mutationOptions?: MutOpts<"post", "/facilities/">
) => {
  return useMutation({
    mutationKey: facilityKeys.create(),
    mutationFn: fetchOpts => {
      return api.POST("/facilities/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: facilityKeys.list(),
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const useUpdateFacilityMutation = (
  mutationOptions?: MutOpts<"patch", "/facilities/{id}/">
) => {
  return useMutation({
    mutationKey: facilityKeys.update(),
    mutationFn: fetchOpts => {
      return api.PATCH("/facilities/{id}/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: facilityKeys.list(),
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

// /adt/facility-geofences/ is not yet in the generated schema — run `bun api:sync` when the backend is deployed
type FacilityGeofence = {
  id?: number
  facility: number
  latitude: string
  longitude: string
  radiusMeters?: number | null
  isActive?: boolean | null
}

type GeofenceBody = { body: Omit<FacilityGeofence, "id"> }

const geofenceApi = api as unknown as {
  GET(path: string, opts: { params: { query: { facility: number } } }): Promise<{ data?: FacilityGeofence | null }>
  PUT(path: string, opts: GeofenceBody): Promise<{ data?: FacilityGeofence | null }>
}

export const useFacilityGeofenceQuery = (
  facilityId: number,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: facilityKeys.geofence(facilityId),
    queryFn: () =>
      geofenceApi
        .GET("/adt/facility-geofences/", {
          params: { query: { facility: facilityId } },
        })
        .then(({ data }) => data ?? null),
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
    retry: (failureCount, error: unknown) =>
      (error as { status?: number })?.status !== 404 && failureCount < 2,
  })
}

export const useUpsertFacilityGeofenceMutation = (
  mutationOptions?: UseMutationOptions<
    { data?: FacilityGeofence | null },
    unknown,
    GeofenceBody,
    unknown
  >
) => {
  return useMutation({
    mutationFn: (fetchOpts: GeofenceBody) =>
      geofenceApi.PUT("/adt/facility-geofences/", fetchOpts),
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      const facilityId = data?.data?.facility
      if (typeof facilityId === "number") {
        queryClient.invalidateQueries({
          queryKey: facilityKeys.geofence(facilityId),
        })
      }
      return mutationOptions?.onSuccess?.(data, variables, context)
    },
  })
}

export const useDeleteFacilityMutation = (
  mutationOptions?: MutOpts<"delete", "/facilities/{id}/">
) => {
  return useMutation({
    mutationKey: facilityKeys.update(),
    mutationFn: fetchOpts => {
      return api.DELETE("/facilities/{id}/", fetchOpts)
    },

    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: facilityKeys.list(),
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}
